
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { TelemetryPoint, Finding, Severity } from '../types';
import { calculateAssetGeometry, E3_HARDWARE_H_FOV, E3_HARDWARE_V_FOV, E3_EFFECTIVE_FOV_MULTIPLIER, calculateSurfaceFootprint, E3_CAMERA_WALL_OFFSET } from '../services/telemetryService';

interface TrajectoryMapProps {
  points: TelemetryPoint[];
  findings?: Finding[];
  onClose: () => void;
  range?: [number, number];
  modelPoints?: TelemetryPoint[];
  previewMode?: 'MODEL' | 'PATH' | 'BOTH';
  mapMode?: 'ANALYSIS' | 'PLANNING';
  minimal?: boolean;
  currentTime?: number;
  baseTimestamp?: number;
  targetOverlap?: number;
  targetStandoff?: number;
  forceShowCaptureLocations?: boolean;
  showCapOnly?: boolean;
  hideFrustrums?: boolean;
  showGuides?: boolean;
  overrideGeometry?: {
    center: { x: number, y: number };
    b1?: { z: number, r: number };
    c1?: { z: number, r: number };
    c2?: { z: number, r: number };
    r1?: { z: number, r: number };
    radius?: number;
    baseZ?: number;
    coneHeight?: number;
    cylinderHeight?: number;
    coneBottomRadius?: number;
  };
  showDroneModel?: boolean;
  posOverride?: { x: number, y: number, z: number } | null;
  hideLines?: boolean;
  initialCloudOffset?: { x: number, y: number };
  onAlignChange?: (offset: { x: number, y: number }) => void;
}

const TAN_H = Math.tan(((E3_HARDWARE_H_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) / 2) * (Math.PI / 180));
const TAN_V = Math.tan(((E3_HARDWARE_V_FOV * E3_EFFECTIVE_FOV_MULTIPLIER) / 2) * (Math.PI / 180));

const applyQuaternion = (p: { x: number, y: number, z: number }, q: { x: number, y: number, z: number, w: number }) => {
  const { x, y, z } = p, { x: qx, y: qy, z: qz, w: qw } = q;
  const tx = 2 * (qy * z - qz * y), ty = 2 * (qz * x - qx * z), tz = 2 * (qx * y - qy * x);
  return { x: x + qw * tx + (qy * tz - qz * ty), y: y + qw * ty + (qz * tx - qx * tz), z: z + qw * tz + (qx * ty - qy * tx) };
};

export const TrajectoryMapM: React.FC<TrajectoryMapProps> = ({
  points, findings = [], onClose, range, modelPoints = [], previewMode = 'BOTH', mapMode = 'ANALYSIS', minimal = false, currentTime, baseTimestamp, targetOverlap, targetStandoff, forceShowCaptureLocations, showCapOnly, hideFrustrums, showGuides = false, overrideGeometry, showDroneModel, posOverride, hideLines, initialCloudOffset, onAlignChange
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [viewAngle, setViewAngle] = useState({ pitch: 90, yaw: 0 });
  const [zoom, setZoom] = useState(1.0);
  const [focusCoord, setFocusCoord] = useState<{ x: number, y: number, z: number } | null>(null);
  const [interactionMode, setInteractionMode] = useState<'ORBIT' | 'PAN'>('ORBIT');
  const [pointDensity, setPointDensity] = useState(50);

  const [hasInitializedView, setHasInitializedView] = useState(false);
  const [cloudOffset, setCloudOffset] = useState(initialCloudOffset || { x: 0, y: 0 });
  const [isAligning, setIsAligning] = useState(false);

  const velocityRef = useRef({ pitch: 0, yaw: 0 });
  const isDraggingRef = useRef(false);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const requestRef = useRef<number | null>(null);

  const logStart = useMemo(() => points.length > 0 ? points[0].timestamp : 0, [points]);
  const displayTime = currentTime !== undefined ? currentTime : 0;
  const floorZ = useMemo(() => points.length > 0 ? points[0].z : 0, [points]);

  const assetGeometry = useMemo(() => {
    if (overrideGeometry) {
      const { b1, c1, c2, r1 } = overrideGeometry;
      if (b1 && c1 && c2 && r1) {
        return {
          centerX: overrideGeometry.center.x,
          centerY: overrideGeometry.center.y,
          radius: c2.r,
          diameter: c2.r * 2,
          minZ: b1.z,
          maxZ: r1.z,
          totalHeight: r1.z - b1.z,
          groundZ: b1.z,
          b1: b1,
          c1: c1,
          c2: c2,
          r1: r1,
          legacy: false
        };
      }
      const radius = overrideGeometry.radius || 5;
      const baseZ = overrideGeometry.baseZ || 0;
      const coneHeight = overrideGeometry.coneHeight || 0;
      const cylinderHeight = overrideGeometry.cylinderHeight || 10;
      const coneBottomRadius = overrideGeometry.coneBottomRadius || 0.5;
      return {
        centerX: overrideGeometry.center.x,
        centerY: overrideGeometry.center.y,
        radius,
        diameter: radius * 2,
        minZ: baseZ,
        maxZ: baseZ + coneHeight + cylinderHeight,
        totalHeight: coneHeight + cylinderHeight,
        groundZ: baseZ,
        b1: { z: baseZ, r: coneBottomRadius },
        c1: { z: baseZ + coneHeight, r: radius },
        c2: { z: baseZ + coneHeight + cylinderHeight, r: radius },
        r1: { z: baseZ + coneHeight + cylinderHeight + 1.5, r: radius },
        legacy: true
      };
    }
    const geom = calculateAssetGeometry(points);
    return {
      ...geom,
      centerX: geom.center.x,
      centerY: geom.center.y,
      diameter: geom.radius * 2,
      groundZ: floorZ,
      b1: { z: geom.minZ, r: geom.coneBottomRadius },
      c1: { z: geom.cylinderBottomZ, r: geom.radius },
      c2: { z: geom.maxZ, r: geom.radius },
      r1: { z: geom.maxZ + 1.5, r: geom.radius },
      legacy: true
    };
  }, [points, overrideGeometry, floorZ, modelPoints]);

  const getLocalWallRadius = useCallback((z: number) => {
    const { b1, c1, c2, r1 } = assetGeometry;
    if (z <= b1.z) return b1.r;
    if (z <= c1.z) return b1.r + (c1.r - b1.r) * ((z - b1.z) / Math.max(0.01, c1.z - b1.z));
    if (z <= c2.z) return c1.r + (c2.r - c1.r) * ((z - c1.z) / Math.max(0.01, c2.z - c1.z));
    return c2.r + (r1.r - c2.r) * ((z - c2.z) / Math.max(0.01, r1.z - c2.z));
  }, [assetGeometry]);

  const captureIndices = useMemo(() => {
    if (!points || points.length === 0) return [];
    let lastIdx = 0, indices: number[] = [0];
    const strideScale = Math.max(0.01, 1 - ((targetOverlap ?? 25) / 100));

    for (let i = 1; i < points.length; i++) {
      const p = points[i];
      const pPrev = points[lastIdx];
      if (p.isTransit) continue;
      if (p.forceCapture) { indices.push(i); lastIdx = i; continue; }
      const dx_c = p.x - assetGeometry.centerX, dy_c = p.y - assetGeometry.centerY, distFromCenter = Math.sqrt(dx_c * dx_c + dy_c * dy_c);
      const wallR = getLocalWallRadius(p.z);
      const standoffAtP = p.lookInward
        ? Math.max(0.1, (distFromCenter + wallR) - E3_CAMERA_WALL_OFFSET)
        : Math.max(0.1, Math.abs(wallR - distFromCenter) - E3_CAMERA_WALL_OFFSET);
      const frameW = 2 * standoffAtP * TAN_H, frameH = 2 * standoffAtP * TAN_V;
      const dx = p.x - pPrev.x, dy = p.y - pPrev.y, dz = p.z - pPrev.z, totalDist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const vLast = applyQuaternion({ x: 1, y: 0, z: 0 }, { x: pPrev.qx, y: pPrev.qy, z: pPrev.qz, w: pPrev.qw });
      const vCurr = applyQuaternion({ x: 1, y: 0, z: 0 }, { x: p.qx, y: p.qy, z: p.qz, w: p.qw });
      const angleShift = Math.acos(Math.min(1, Math.max(-1, vLast.x * vCurr.x + vLast.y * vCurr.y + vLast.z * vCurr.z))) * (180 / Math.PI);
      const crossedPole = distFromCenter < 0.25 && Math.sqrt(Math.pow(pPrev.x - assetGeometry.centerX, 2) + Math.pow(pPrev.y - assetGeometry.centerY, 2)) >= 0.25;
      const dH = Math.sqrt(dx * dx + dy * dy), dV = Math.abs(dz);
      const criticalStride = dV >= dH ? frameH : frameW;
      if (totalDist >= criticalStride * strideScale || angleShift > 25 || crossedPole) { indices.push(i); lastIdx = i; }
    }
    return indices;
  }, [points, targetOverlap, assetGeometry, getLocalWallRadius]);

  const activeCaptureIndex = useMemo(() => {
    // If mission time is near 0.0s, treat as idle/not-started state.
    if (displayTime === undefined || captureIndices.length === 0 || displayTime < 0.01) return -1;
    let bestIdx = -1;
    const tBase = baseTimestamp !== undefined ? baseTimestamp : logStart;
    for (let i = 0; i < captureIndices.length; i++) {
      if (points[captureIndices[i]].timestamp - tBase <= displayTime + 0.05) {
        bestIdx = i;
      } else {
        break;
      }
    }
    return bestIdx;
  }, [displayTime, captureIndices, points, logStart, baseTimestamp]);

  const activeFootprint = useMemo(() => {
    if (displayTime === undefined || points.length === 0) return null;
    const opticalStandoff = Math.max(0.01, (targetStandoff || 0.8));
    return { standoff: opticalStandoff, ...calculateSurfaceFootprint(opticalStandoff, assetGeometry.radius), standoffCm: Math.round((targetStandoff || 0.8) * 100) };
  }, [displayTime, points, assetGeometry, targetStandoff]);

  const viewFocus = useMemo(() => ({ x: assetGeometry.centerX, y: assetGeometry.centerY, z: (assetGeometry.minZ + assetGeometry.maxZ) / 2 }), [assetGeometry]);
  const zeroHeading = useMemo(() => points.length > 0 ? Math.atan2(points[0].y - assetGeometry.centerY, points[0].x - assetGeometry.centerX) : 0, [points, assetGeometry]);

  const setView = useCallback((type: 'Top' | 'Front' | 'Side') => {
    const zeroDeg = (zeroHeading * 180 / Math.PI);
    if (type === 'Top') { setViewAngle({ pitch: 180, yaw: 90 - zeroDeg }); setZoom(1.4); }
    else if (type === 'Front') { setViewAngle({ pitch: 90, yaw: 90 - zeroDeg }); setZoom(1.6); }
    else if (type === 'Side') { setViewAngle({ pitch: 90, yaw: -zeroDeg }); setZoom(1.6); }
  }, [zeroHeading]);

  useEffect(() => {
    if (!hasInitializedView && points.length > 2) {
      const timer = setTimeout(() => {
        setView('Side');
        setHasInitializedView(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [points, setView, hasInitializedView]);



  const render = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const dpr = window.devicePixelRatio || 1, rect = canvas.getBoundingClientRect();
    if (canvas.width !== Math.round(rect.width * dpr) || canvas.height !== Math.round(rect.height * dpr)) { canvas.width = Math.round(rect.width * dpr); canvas.height = Math.round(rect.height * dpr); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.scale(dpr, dpr); }
    if (!isDraggingRef.current && interactionMode === 'ORBIT' && (Math.abs(velocityRef.current.pitch) > 0.01 || Math.abs(velocityRef.current.yaw) > 0.01)) { setViewAngle(p => ({ pitch: p.pitch + velocityRef.current.pitch, yaw: p.yaw + velocityRef.current.yaw })); velocityRef.current.pitch *= 0.94; velocityRef.current.yaw *= 0.94; }

    ctx.fillStyle = '#020617'; ctx.fillRect(0, 0, rect.width, rect.height);
    const baseScale = Math.min(rect.width, rect.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
    const centerPoint = focusCoord || viewFocus, rY = (viewAngle.yaw * Math.PI) / 180, rP = (viewAngle.pitch * Math.PI) / 180, cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.sin(rP), midX = rect.width / 2, midY = rect.height / 2;

    const project = (px: number, py: number, pz: number) => {
      const dx = px - centerPoint.x, dy = py - centerPoint.y, dz = pz - centerPoint.z;
      const nx = -dx * baseScale, ny = -dy * baseScale, nz = dz * baseScale;
      const rx = nx * cosY - ny * sinY, ry1 = nx * sinY + ny * cosY, ry = ry1 * cosP - nz * sinP, rz = ry1 * sinP + nz * cosP;
      return { x: midX + rx, y: midY + ry, rz };
    };

    const drawFrustum = (pos: { x: number, y: number, z: number }, rot: { x: number, y: number, z: number, w: number }, color: string, isSolid: boolean = false, thick: number = 1) => {
      const vLook = applyQuaternion({ x: 1, y: 0, z: 0 }, rot);
      let opticalL = 1.0;
      if (Math.abs(vLook.z) > 0.707) {
        const boundaryZ = vLook.z < 0 ? assetGeometry.minZ : assetGeometry.maxZ;
        opticalL = Math.abs(boundaryZ - pos.z) / Math.max(0.1, Math.abs(vLook.z));
      } else {
        const dx_c = pos.x - assetGeometry.centerX, dy_c = pos.y - assetGeometry.centerY;
        const distFromCenter = Math.sqrt(dx_c * dx_c + dy_c * dy_c);
        const localWallR = getLocalWallRadius(pos.z);
        const isInward = (vLook.x * (-dx_c) + vLook.y * (-dy_c)) > 0.1;
        opticalL = isInward ? (distFromCenter + localWallR) : Math.abs(localWallR - distFromCenter);
      }

      opticalL = Math.min(15.0, Math.max(0.01, opticalL - E3_CAMERA_WALL_OFFSET));

      const hw = opticalL * TAN_H, hh = opticalL * TAN_V, cameraOffset = applyQuaternion({ x: E3_CAMERA_WALL_OFFSET, y: 0, z: 0 }, rot);
      const camX = pos.x + cameraOffset.x, camY = pos.y + cameraOffset.y, camZ = pos.z + cameraOffset.z;
      const verts = [{ x: 0, y: 0, z: 0 }, { x: opticalL, y: -hw, z: -hh }, { x: opticalL, y: hw, z: -hh }, { x: opticalL, y: hw, z: hh }, { x: opticalL, y: -hw, z: hh }].map(v => { const r = applyQuaternion(v, rot); return project(camX + r.x, camY + r.y, camZ + r.z); });

      ctx.strokeStyle = 'rgba(245, 158, 11, 0.5)';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      for (let i = 1; i <= 4; i++) {
        ctx.moveTo(verts[0].x, verts[0].y);
        ctx.lineTo(verts[i].x, verts[i].y);
      }
      ctx.stroke();

      ctx.strokeStyle = color;
      ctx.lineWidth = isSolid ? thick * 1.5 : thick;
      ctx.beginPath();
      ctx.moveTo(verts[1].x, verts[1].y);
      ctx.lineTo(verts[2].x, verts[2].y);
      ctx.lineTo(verts[3].x, verts[3].y);
      ctx.lineTo(verts[4].x, verts[4].y);
      ctx.lineTo(verts[1].x, verts[1].y);
      ctx.stroke();

      if (isSolid) { ctx.globalAlpha = 0.2; ctx.fillStyle = color; ctx.fill(); ctx.globalAlpha = 1.0; }
    };

    const drawDrone = (pos: { x: number, y: number, z: number }, rot: { x: number, y: number, z: number, w: number }) => {
      const RX = 0.25; // Horizontal Radius (25cm)
      const RY = 0.25;
      const RZ = 0.225; // Vertical Radius (22.5cm)
      const bodySize = 0.08;

      // 1. Geodesic Protective Cage (Ellipsoidal)
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 0.8;

      // Draw Longitudinal Ribs (6 rings)
      for (let r = 0; r < 6; r++) {
        const yawOffset = (r * Math.PI) / 6;
        ctx.beginPath();
        for (let i = 0; i <= 32; i++) {
          const t = (i / 32) * Math.PI * 2;
          // Ellipsoidal coordinates: x=RX*cos(t)*cos(yaw), y=RY*cos(t)*sin(yaw), z=RZ*sin(t)
          const v = {
            x: Math.cos(t) * RX * Math.cos(yawOffset),
            y: Math.cos(t) * RY * Math.sin(yawOffset),
            z: Math.sin(t) * RZ
          };
          const rv = applyQuaternion(v, rot);
          const p = project(pos.x + rv.x, pos.y + rv.y, pos.z + rv.z);
          if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }

      // Draw Latitudinal Rings
      const latAngles = [-Math.PI / 4, 0, Math.PI / 4];
      latAngles.forEach(lat => {
        const rLatX = RX * Math.cos(lat);
        const rLatY = RY * Math.cos(lat);
        const zLat = RZ * Math.sin(lat);
        ctx.beginPath();
        for (let i = 0; i <= 32; i++) {
          const t = (i / 32) * Math.PI * 2;
          const v = { x: Math.cos(t) * rLatX, y: Math.sin(t) * rLatY, z: zLat };
          const rv = applyQuaternion(v, rot);
          const p = project(pos.x + rv.x, pos.y + rv.y, pos.z + rv.z);
          if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      });

      // 2. Internal Support Arms (X-Frame)
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(100, 116, 139, 0.6)';
      ctx.lineWidth = 1.8;
      const armAngles = [Math.PI / 4, 3 * Math.PI / 4, 5 * Math.PI / 4, 7 * Math.PI / 4];
      armAngles.forEach(a => {
        const vEnd = { x: Math.cos(a) * RX * 0.9, y: Math.sin(a) * RY * 0.9, z: 0 };
        const p1 = project(pos.x, pos.y, pos.z);
        const rv2 = applyQuaternion(vEnd, rot);
        const p2 = project(pos.x + rv2.x, pos.y + rv2.y, pos.z + rv2.z);
        ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y);
      });
      ctx.stroke();

      // 3. Central Avionics Core (Hexagonal Prism)
      const coreVerts = [];
      const coreH = bodySize * 1.5;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        coreVerts.push({ x: Math.cos(a) * bodySize, y: Math.sin(a) * bodySize, z: -coreH / 2 });
        coreVerts.push({ x: Math.cos(a) * bodySize, y: Math.sin(a) * bodySize, z: coreH / 2 });
      }

      ctx.beginPath();
      ctx.strokeStyle = '#06b6d4';
      ctx.lineWidth = 2;
      for (let i = 0; i < 12; i += 2) {
        const p1 = project(...Object.values(applyQuaternion(coreVerts[i], rot)).map((v, idx) => v + [pos.x, pos.y, pos.z][idx]) as [number, number, number]);
        const p2 = project(...Object.values(applyQuaternion(coreVerts[i + 1], rot)).map((v, idx) => v + [pos.x, pos.y, pos.z][idx]) as [number, number, number]);
        const nextIdx = (i + 2) % 12;
        const p3 = project(...Object.values(applyQuaternion(coreVerts[nextIdx], rot)).map((v, idx) => v + [pos.x, pos.y, pos.z][idx]) as [number, number, number]);
        ctx.moveTo(p1.x, p1.y); ctx.lineTo(p2.x, p2.y); // Vertical
        ctx.moveTo(p1.x, p1.y); ctx.lineTo(p3.x, p3.y); // Bottom Ring
      }
      ctx.stroke();

      // 4. LIDAR Pod (Top mounted)
      const lidarV = applyQuaternion({ x: 0, y: 0, z: coreH / 2 + 0.02 }, rot);
      const lp = project(pos.x + lidarV.x, pos.y + lidarV.y, pos.z + lidarV.z);
      ctx.fillStyle = '#334155'; ctx.beginPath(); ctx.arc(lp.x, lp.y, 4, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1; ctx.stroke();

      // 5. Camera & LED Lighting (Front)
      const camPos = applyQuaternion({ x: bodySize + 0.02, y: 0, z: 0 }, rot);
      const cp = project(pos.x + camPos.x, pos.y + camPos.y, pos.z + camPos.z);
      ctx.fillStyle = '#f59e0b'; ctx.beginPath(); ctx.arc(cp.x, cp.y, 3, 0, Math.PI * 2); ctx.fill();

      // Front LEDs
      [-0.04, 0.04].forEach(offsetX => {
        const ledV = applyQuaternion({ x: bodySize, y: offsetX, z: 0.03 }, rot);
        const ldp = project(pos.x + ledV.x, pos.y + ledV.y, pos.z + ledV.z);
        ctx.fillStyle = '#fff'; ctx.shadowBlur = 10; ctx.shadowColor = '#fff';
        ctx.beginPath(); ctx.arc(ldp.x, ldp.y, 1.5, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      });
    };

    if (points.length > 0 || overrideGeometry) {
      const { centerX, centerY, radius } = assetGeometry, groundSize = radius * 4.5, corners = [{ x: centerX - groundSize, y: centerY - groundSize }, { x: centerX + groundSize, y: centerY - groundSize }, { x: centerX + groundSize, y: centerY + groundSize }, { x: centerX - groundSize, y: centerY + groundSize }].map(c => project(c.x, c.y, floorZ));
      ctx.beginPath(); ctx.moveTo(corners[0].x, corners[0].y); ctx.lineTo(corners[1].x, corners[1].y); ctx.lineTo(corners[2].x, corners[2].y); ctx.lineTo(corners[3].x, corners[3].y); ctx.closePath(); ctx.fillStyle = 'rgba(6, 182, 212, 0.1)'; ctx.fill(); ctx.strokeStyle = 'rgba(6, 182, 212, 0.4)'; ctx.lineWidth = 1; ctx.stroke();
      const startPt = points[0], ringR = radius * 1.5;
      if (startPt && showGuides) {
        // Radius Circle & Degree Labels
        ctx.beginPath();
        for (let i = 0; i <= 90; i++) {
          const t = (i / 90) * Math.PI * 2, p = project(centerX + Math.cos(t) * ringR, centerY + Math.sin(t) * ringR, floorZ);
          if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
        }
        ctx.setLineDash([8, 8]); ctx.strokeStyle = 'rgba(6, 182, 212, 0.6)'; ctx.lineWidth = 2.0; ctx.stroke(); ctx.setLineDash([]);
        ctx.font = 'bold 12px JetBrains Mono'; ctx.fillStyle = 'rgba(6, 182, 212, 0.9)'; ctx.textAlign = 'center';
        for (let d = 0; d < 360; d += 45) {
          const theta = zeroHeading - (d * Math.PI / 180);
          const p = project(centerX + Math.cos(theta) * ringR, centerY + Math.sin(theta) * ringR, floorZ);
          ctx.fillText(`${d}°`, p.x, p.y);
        }

        const startLblPos = project(startPt.x, startPt.y, startPt.z);
        ctx.fillStyle = '#fff'; ctx.font = 'bold 14px JetBrains Mono'; ctx.textAlign = 'left'; ctx.fillText('MISSION START', startLblPos.x + 20, startLblPos.y);
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(startLblPos.x, startLblPos.y); ctx.lineTo(startLblPos.x + 15, startLblPos.y); ctx.stroke();

        // Height Indicator Gauge
        if (Math.abs((viewAngle.pitch % 180) - 90) <= 45) {
          const gaugeX = centerX - radius * 2.0, gaugeY = centerY;
          const pB = project(gaugeX, gaugeY, assetGeometry.minZ), pT = project(gaugeX, gaugeY, assetGeometry.maxZ);
          ctx.beginPath(); ctx.strokeStyle = 'rgba(6, 182, 212, 0.6)'; ctx.lineWidth = 3;
          ctx.moveTo(pB.x, pB.y); ctx.lineTo(pT.x, pT.y); ctx.stroke();
          ctx.fillStyle = 'rgba(6, 182, 212, 0.9)'; ctx.font = 'bold 12px JetBrains Mono';
          for (let z = Math.floor(assetGeometry.minZ); z <= Math.ceil(assetGeometry.maxZ); z++) {
            const p = project(gaugeX, gaugeY, z);
            ctx.beginPath(); ctx.lineWidth = 2;
            ctx.moveTo(p.x - 6, p.y); ctx.lineTo(p.x + 6, p.y); ctx.stroke();
            ctx.fillText(`${z}m`, p.x - 25, p.y + 4);
          }
        }
      }
    }

    if ((previewMode === 'BOTH' || previewMode === 'MODEL') && modelPoints && modelPoints.length > 0) {
      const densityFactor = Math.max(1, Math.ceil((100 - pointDensity) / 10));
      const skip = isDraggingRef.current ? Math.max(densityFactor, Math.ceil(modelPoints.length / 20000)) : densityFactor;
      ctx.beginPath(); ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      for (let i = 0; i < modelPoints.length; i += skip) { const p = modelPoints[i], pr = project(p.x + cloudOffset.x, p.y + cloudOffset.y, p.z); ctx.rect(pr.x - 0.5, pr.y - 0.5, 1, 1); } ctx.fill();
    }

    if (points.length > 0 && !hideLines) { ctx.beginPath(); ctx.lineWidth = 2.0; ctx.strokeStyle = '#06b6d4'; if (mapMode === 'PLANNING') ctx.setLineDash([5, 5]); points.forEach((p, i) => { const pr = project(p.x, p.y, p.z); if (i === 0) ctx.moveTo(pr.x, pr.y); else ctx.lineTo(pr.x, pr.y); }); ctx.stroke(); ctx.setLineDash([]); }

    if (mapMode === 'PLANNING' || overrideGeometry) {
      const { centerX, centerY, b1, c1, c2, r1 } = assetGeometry;
      const drawC = (z: number, r: number, label: string) => {
        ctx.beginPath();
        for (let i = 0; i <= 64; i++) {
          const t = (i / 64) * Math.PI * 2, p = project(centerX + Math.cos(t) * r, centerY + Math.sin(t) * r, z);
          if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
        }
        ctx.strokeStyle = 'rgba(6,182,212,0.8)';
        ctx.lineWidth = 2;
        ctx.stroke();

        const lp = project(centerX + r, centerY, z);
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 12px JetBrains Mono';
        ctx.textAlign = 'left';
        ctx.fillText(label, lp.x + 5, lp.y);
      };
      drawC(b1.z, b1.r, 'B1');
      drawC(c1.z, c1.r, 'C1');
      drawC(c2.z, c2.r, 'C2');
      drawC(r1.z, r1.r, 'R1');
    }

    if (!hideFrustrums) {
      captureIndices.forEach((idx, cIdx) => {
        const p = points[idx], rot = { x: p.qx, y: p.qy, z: p.qz, w: p.qw };

        // Determine color based on current simulation progress
        const isAct = activeCaptureIndex === cIdx;
        const isPast = activeCaptureIndex !== -1 && cIdx < activeCaptureIndex;

        if (showCapOnly && !isPast && !isAct) return;

        if (isAct) {
          drawFrustum({ x: p.x, y: p.y, z: p.z }, rot, `rgba(34, 197, 94, ${0.5 + (Math.sin(Date.now() / 150) + 1) / 2 * 0.5})`, true, 2.5);
        } else if (isPast) {
          const color = 'rgba(34, 197, 94, 0.6)';
          if (range && (p.timestamp - (baseTimestamp || logStart) < range[0] || p.timestamp - (baseTimestamp || logStart) > range[1])) return;
          drawFrustum({ x: p.x, y: p.y, z: p.z }, rot, color, false, 1.5);
        } else {
          // PLANNED / FUTURE FRUSTUMS
          const color = 'rgba(250, 204, 21, 0.6)';
          if (range && (p.timestamp - (baseTimestamp || logStart) < range[0] || p.timestamp - (baseTimestamp || logStart) > range[1])) return;
          drawFrustum({ x: p.x, y: p.y, z: p.z }, rot, color, false, 1.0);
        }
      });
    }

    if (posOverride || (displayTime !== undefined && points.length > 0)) {
      let rawPos = { x: 0, y: 0, z: 0 };
      let rot = { x: 0, y: 0, z: 0, w: 1 };
      let isTransit = false;

      if (posOverride) {
        rawPos = posOverride;
        isTransit = true;
      } else {
        const tT = displayTime + (baseTimestamp !== undefined ? baseTimestamp : logStart);
        let p1 = points[0], p2 = points[0], lerp = 0;
        if (points.length > 1) {
          let l = 0, h = points.length - 1;
          if (tT >= points[h].timestamp) { p1 = points[h]; p2 = points[h]; }
          else if (tT <= points[l].timestamp) { p1 = points[l]; p2 = points[l]; }
          else {
            while (l <= h) { const m = (l + h) >>> 1; if (points[m].timestamp <= tT) l = m + 1; else h = m - 1; }
            const idx = Math.max(0, l - 1); p1 = points[idx]; p2 = points[idx + 1] || points[idx];
            const span = p2.timestamp - p1.timestamp; if (span > 0.001) lerp = (tT - p1.timestamp) / span;
          }
        }
        rawPos = { x: p1.x + (p2.x - p1.x) * lerp, y: p1.y + (p2.y - p1.y) * lerp, z: p1.z + (p2.z - p1.z) * lerp };
        let dotQ = p1.qx * p2.qx + p1.qy * p2.qy + p1.qz * p2.qz + p1.qw * p2.qw;
        const sign = dotQ < 0 ? -1 : 1;
        rot = { x: p1.qx + sign * (p2.qx - p1.qx) * lerp, y: p1.qy + sign * (p2.qy - p1.qy) * lerp, z: p1.qz + sign * (p2.qz - p1.qz) * lerp, w: p1.qw + sign * (p2.qw - p1.qw) * lerp };
        const mag = Math.sqrt(rot.x ** 2 + rot.y ** 2 + rot.z ** 2 + rot.w ** 2);
        rot.x /= mag; rot.y /= mag; rot.z /= mag; rot.w /= mag;
        isTransit = p1.isTransit;
      }

      const uav = project(rawPos.x, rawPos.y, rawPos.z);
      if (!isTransit) {
        ctx.save(); ctx.globalAlpha = 0.8; drawFrustum(rawPos, rot, '#06b6d4', false, 3); ctx.restore();
      }

      if (showDroneModel) {
        drawDrone(rawPos, rot);
      } else {
        ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(uav.x, uav.y, 6, 0, Math.PI * 2); ctx.fill();
      }
    }
  }, [points, viewAngle, zoom, focusCoord, viewFocus, assetGeometry, displayTime, logStart, baseTimestamp, range, floorZ, previewMode, mapMode, overrideGeometry, captureIndices, activeCaptureIndex, showCapOnly, hideFrustrums, targetStandoff, getLocalWallRadius, cloudOffset, modelPoints, zeroHeading, forceShowCaptureLocations, showDroneModel, pointDensity]);

  useEffect(() => { requestRef.current = requestAnimationFrame(function frame() { render(); requestRef.current = requestAnimationFrame(frame); }); return () => { if (requestRef.current) cancelAnimationFrame(requestRef.current); }; }, [render]);

  return (
    <div className="relative w-full h-full bg-[#020617] overflow-hidden rounded-none shadow-xl border border-white/5">
      <canvas
        ref={canvasRef}
        onContextMenu={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          isDraggingRef.current = true;
          lastMousePosRef.current = { x: e.clientX, y: e.clientY };
          velocityRef.current = { pitch: 0, yaw: 0 };
          (e.currentTarget as any)._dragButton = e.button;
        }}
        onMouseMove={(e) => {
          if (!isDraggingRef.current) return;
          const dx = e.clientX - lastMousePosRef.current.x, dy = e.clientY - lastMousePosRef.current.y;
          const button = (e.currentTarget as any)._dragButton;

          if (isAligning) {
            const cvs = canvasRef.current;
            if (cvs) {
              const baseScale = Math.min(cvs.width, cvs.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
              setCloudOffset(p => ({ x: p.x + dx / baseScale, y: p.y - dy / baseScale }));
            }
          } else if (button === 2) {
            // RIGHT CLICK -> PAN
            const cvs = canvasRef.current;
            if (cvs) {
              const baseScale = Math.min(cvs.width, cvs.height) / Math.max(assetGeometry.diameter, assetGeometry.totalHeight, 1) * 0.6 * zoom;
              const rY = (viewAngle.yaw * Math.PI) / 180, rP = (viewAngle.pitch * Math.PI) / 180;
              const cosY = Math.cos(rY), sinY = Math.sin(rY), cosP = Math.cos(rP), sinP = Math.cos(rP);

              const dCx = (dx * -cosY + dy * -sinY * cosP) / baseScale;
              const dCy = (dx * sinY + dy * -cosY * cosP) / baseScale;
              const dCz = (dy * sinP) / baseScale;

              setFocusCoord(p => {
                const base = p || viewFocus;
                // Reversing the pan mapping so map moves with cursor natively (Z is inverted)
                return { x: base.x - dCx, y: base.y - dCy, z: base.z + dCz };
              });
            }
          } else {
            // LEFT CLICK -> ORBIT
            velocityRef.current = { yaw: dx * 0.4, pitch: dy * 0.4 };
            setViewAngle(p => ({ yaw: p.yaw + dx * 0.4, pitch: p.pitch + dy * 0.4 }));
          }
          lastMousePosRef.current = { x: e.clientX, y: e.clientY };
        }}
        onMouseUp={() => isDraggingRef.current = false}
        onWheel={(e) => setZoom(p => Math.min(Math.max(p * (e.deltaY > 0 ? 0.92 : 1.08), 0.1), 10))}
        className={`w-full h-full ${isAligning ? 'cursor-move' : 'cursor-grab'}`}
      />
      <button 
        data-map-center-align 
        style={{ display: 'none' }} 
        onClick={() => {
            if (isAligning) {
                onAlignChange?.(cloudOffset); // Save configuration when exiting center-align mode
            }
            setIsAligning(!isAligning);
        }} 
      />
      <div className="absolute top-4 right-4 flex items-center gap-1 bg-black/60 backdrop-blur-md p-1 rounded-none border border-white/10 z-50">
        <button data-map-view="Side" onClick={() => setView('Side')} className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-300/5 text-slate-300 hover:text-white transition">Side</button>
        <button data-map-view="Front" onClick={() => setView('Front')} className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-300/5 text-slate-300 hover:text-white transition">Front</button>
        <button data-map-view="Top" onClick={() => setView('Top')} className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-300/5 text-slate-300 hover:text-white transition">Top</button>
        <div className="flex items-center gap-2 px-2 border-l border-white/10">
          <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">PTS</span>
          <input
            type="range" min={1} max={100} value={pointDensity}
            onChange={e => setPointDensity(Number(e.target.value))}
            className="w-20 h-1 accent-[#0000a9] cursor-pointer"
          />
          <span className="text-[9px] font-mono text-slate-400 w-6 text-right">{pointDensity}</span>
        </div>
      </div>
      <div className="absolute top-6 left-6 flex flex-col gap-3 z-50 pointer-events-none">
        {activeFootprint && (
          <>
            <div className="bg-black/80 backdrop-blur-md px-5 py-4 rounded-none border border-white/10 text-left animate-slideLeft shadow-2xl min-w-[160px]"><span className="text-[9px] text-slate-400 font-black uppercase block mb-1">Standoff (D)</span><span className="text-base font-mono font-black text-slate-200">{activeFootprint.standoffCm} cm</span></div>
            <div className="bg-black/80 backdrop-blur-md px-5 py-4 rounded-none border border-white/10 text-left animate-slideLeft shadow-2xl min-w-[160px]"><span className="text-[9px] text-slate-400 font-black uppercase block mb-1">Surface View</span><span className="text-base font-mono font-black text-white">{activeFootprint.widthCm}x{activeFootprint.heightCm} cm</span></div>
            <div className="bg-black/80 backdrop-blur-md px-5 py-4 rounded-none border border-white/10 text-left animate-slideLeft shadow-2xl min-w-[160px]"><span className="text-[9px] text-slate-400 font-black uppercase block mb-1">4K Detail (GSD)</span><span className="text-base font-mono font-black text-slate-200">{(activeFootprint as any).gsdMm} mm/px</span></div>
          </>
        )}
      </div>
    </div>
  );
};
